/**
 * TwilioSettingsAdmin.tsx — Admin > Integrations > Twilio.
 *
 * Legacy's Twilio Settings (lib/admin/admin-twilio-settings.php): the
 * practice's own Twilio account, which every SMS, call and WhatsApp send uses
 * (the platform's shared account when the practice has none).
 *   - Twilio API Credentials: Account SID, Auth Token, API Key SID, API Key
 *     Secret, TwiML App SID. A stored one shows masked (its last four
 *     characters) with Change; one left blank keeps what is stored. The first
 *     setup needs the Account SID and Auth Token.
 *   - Voice Settings: Forward Number and SIP Domain.
 * Save sits top right, with Unsaved changes and Revert, as on the other
 * settings pages.
 *
 * Locked (plan 2026-10-10-001 U7: R8, R18): the settings show read-only (an
 * AdminLockedFieldset), stored credentials still masked and an unset one as
 * "Not set". There is no Save, Revert or Change, no setup prompt and no
 * required error, and nothing says why.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { DialogField, fieldClasses } from '@/components/admin/DialogChrome';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { getErrorMessage } from '@/services/apiClient';
import {
  TWILIO_CREDENTIALS,
  getTwilioSettings,
  saveTwilioSettings,
  type TwilioCredentialKey,
  type TwilioSettings,
} from '@/services/twilioSettingsAdmin.service';
import { toast } from '@/store/toastStore';

/** Typed into but not shown back: the secrets. */
const SECRET_KEYS: TwilioCredentialKey[] = ['authToken', 'apiKeySecret'];

type Values = Partial<Record<TwilioCredentialKey, string>>;

export function TwilioSettingsAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [settings, setSettings] = useState<TwilioSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState<Values>({});
  const [changing, setChanging] = useState<Set<TwilioCredentialKey>>(new Set());
  const [forwardNumber, setForwardNumber] = useState('');
  const [sipDomain, setSipDomain] = useState('');

  function show(s: TwilioSettings) {
    setSettings(s);
    setValues({});
    setChanging(new Set());
    setForwardNumber(s.forwardNumber);
    setSipDomain(s.sipDomain);
  }

  useEffect(() => {
    let cancelled = false;
    getTwilioSettings()
      .then((s) => !cancelled && show(s))
      .catch((err) => !cancelled && toast.error(getErrorMessage(err, 'Failed to load the Twilio settings')))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const typed = (key: TwilioCredentialKey) => (values[key] ?? '').trim() !== '';
  const dirty =
    settings !== null &&
    (TWILIO_CREDENTIALS.some(({ key }) => typed(key)) || forwardNumber !== settings.forwardNumber || sipDomain !== settings.sipDomain);
  const errors: string[] = [];
  // Locked: nothing can be entered, so nothing is required.
  if (settings && !settings.configured && !locked) {
    if (!typed('accountSid')) errors.push('Account SID is required for the first setup');
    if (!typed('authToken')) errors.push('Auth Token is required for the first setup');
  }

  async function save() {
    if (errors.length > 0 || locked) return;
    setSaving(true);
    try {
      show(await saveTwilioSettings({ ...values, forwardNumber, sipDomain }));
      toast.success('Twilio settings saved');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The Twilio settings could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  function toggleChange(key: TwilioCredentialKey) {
    setChanging((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        setValues((v) => ({ ...v, [key]: '' }));
      } else {
        next.add(key);
      }
      return next;
    });
  }

  // Locked: nothing can change, so there is no Save or Revert.
  const actions = locked ? undefined : (
    <div className="flex flex-wrap items-center gap-3">
      {dirty && (
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-sm text-blue-700">
            <AlertTriangle className="h-4 w-4" aria-hidden />
            Unsaved changes
          </span>
          <Button
            variant="ghost"
            size="xs"
            className="h-7 px-2 text-xs text-slate-500 hover:text-slate-700"
            leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
            aria-label="Revert all changes"
            onClick={() => settings && show(settings)}
          >
            Revert
          </Button>
        </div>
      )}
      <Button onClick={() => void save()} disabled={!dirty || errors.length > 0 || loading || !settings} loading={saving}>
        Save
      </Button>
    </div>
  );

  return (
    <div className="flex h-full flex-col overflow-hidden p-6">
      <div className="flex-shrink-0">
        <PageHeader title="Twilio" subtitle="The practice's own Twilio account for SMS, calls and WhatsApp" actions={actions} />
      </div>
      {loading ? (
        <SpinnerOverlay label="Loading the Twilio settings…" />
      ) : !settings ? (
        <p className="py-16 text-center text-sm text-slate-500">The Twilio settings could not be loaded. Reload the page to try again.</p>
      ) : (
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pb-6">
          {!settings.configured && (
            <p className="max-w-xl rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
              This practice sends through the platform's shared Twilio account.
              {!locked && ' Enter its own Account SID and Auth Token to use its own.'}
            </p>
          )}
          {errors.length > 0 && <p className="max-w-xl text-sm text-red-600">{errors.join('. ')}.</p>}

          <AdminLockedFieldset>
            <section className="max-w-xl space-y-4 rounded-lg border border-slate-200 bg-white p-5">
              <h2 className="text-sm font-semibold text-slate-800">Twilio API Credentials</h2>
              {TWILIO_CREDENTIALS.map(({ key, label }) => {
                const stored = settings.credentials[key];
                const id = `twilio-${key}`;
                const editable = !locked && (!stored.set || changing.has(key));
                const needed = !settings.configured && (key === 'accountSid' || key === 'authToken') && !typed(key);
                return (
                  <DialogField
                    key={key}
                    label={label}
                    htmlFor={editable ? id : undefined}
                    required={!settings.configured && (key === 'accountSid' || key === 'authToken')}
                    state={needed ? 'error' : 'none'}
                    description={stored.set && changing.has(key) ? 'Leave blank to keep the saved one.' : undefined}
                  >
                    <div className="flex items-center gap-2">
                      {stored.set && !changing.has(key) ? (
                        <span className="min-w-0 flex-1 truncate font-mono text-sm text-slate-600" data-testid={`${id}-masked`}>
                          {stored.masked || 'Saved'}
                        </span>
                      ) : locked ? (
                        <span className="min-w-0 flex-1 text-sm text-slate-400">Not set</span>
                      ) : null}
                      {editable && (
                        <Input
                          id={id}
                          type={SECRET_KEYS.includes(key) ? 'password' : 'text'}
                          autoComplete="off"
                          value={values[key] ?? ''}
                          placeholder={stored.set ? `Enter new ${label}` : `Enter ${label}`}
                          maxLength={255}
                          onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                          className={fieldClasses.input(needed ? 'error' : 'none')}
                          wrapperClassName="flex-1"
                        />
                      )}
                      {stored.set && !locked && (
                        <Button variant="secondary" size="xs" onClick={() => toggleChange(key)} aria-label={`${changing.has(key) ? 'Cancel changing' : 'Change'} ${label}`}>
                          {changing.has(key) ? 'Cancel' : 'Change'}
                        </Button>
                      )}
                    </div>
                  </DialogField>
                );
              })}
            </section>

            <section className="max-w-xl space-y-4 rounded-lg border border-slate-200 bg-white p-5">
              <h2 className="text-sm font-semibold text-slate-800">Voice Settings</h2>
              <DialogField label="Forward Number" htmlFor="twilio-forward-number">
                <Input
                  id="twilio-forward-number"
                  value={forwardNumber}
                  maxLength={50}
                  placeholder={locked ? undefined : 'e.g. +18005551234'}
                  onChange={(e) => setForwardNumber(e.target.value)}
                />
              </DialogField>
              <DialogField label="SIP Domain" htmlFor="twilio-sip-domain">
                <Input
                  id="twilio-sip-domain"
                  value={sipDomain}
                  maxLength={255}
                  placeholder={locked ? undefined : 'e.g. yourdomain.sip.twilio.com'}
                  onChange={(e) => setSipDomain(e.target.value)}
                />
              </DialogField>
            </section>
          </AdminLockedFieldset>
        </div>
      )}
    </div>
  );
}
